import { faArrowRight } from '@fortawesome/pro-regular-svg-icons'
import { Card, Box, Flex, Text, Checkbox } from '@mantine/core'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isToday from 'dayjs/plugin/isToday'
import { useCallback, useEffect, useMemo } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useTranslation } from '@flecto/translations'
import { DateTimePickerInput, Icon } from '@flecto/ui'
import useMediaQuery from '@flecto/ui/lib/utils/useMediaQuery'

import { useFormContext } from '../FormCreateInsurancePolicy/FormCreateInsurancePolicy'

dayjs.extend(isToday)
dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)

export function CardSelectCoveragePeriod() {
  const track = useAnalytics('segment')
  const { t, i18n } = useTranslation('saas', { keyPrefix: 'insurances' })

  dayjs.locale(i18n.language)

  const form = useFormContext()

  const { isMobile } = useMediaQuery()

  const getNextHourMinus15 = () => {
    const now = dayjs()
    const next15MinIncrement = now
      .add(15 - (now.minute() % 15), 'minute')
      .startOf('minute')
    const nextHour = next15MinIncrement.add(1, 'hour').startOf('hour')
    const result = nextHour.subtract(30, 'minute')
    return result.format('HH:mm')
  }
  const handleChangeStartAt = useCallback(
    (value: string) => {
      const date = dayjs(value).isBefore(dayjs())
        ? dayjs().format(`YYYY-MM-DDT${getNextHourMinus15()}:00`)
        : value

      if (dayjs(date).isSameOrAfter(form.values.end_at)) {
        const end = dayjs(date).add(30, 'minute').format('YYYY-MM-DDTHH:mm:00')
        form.setFieldValue('end_at', end)
      }

      form.setFieldValue('start_at', date)
    },
    [form.values.end_at, form.values.start_at]
  )

  const handleChangeEndAt = useCallback(
    (value: string) => {
      const selectedDate = dayjs(value)
      const now = dayjs()
      let end

      const isToday = selectedDate.isSame(now, 'day')

      if (form.values.start_at_now) {
        if (isToday) {
          const minTime = now.add(30, 'minute')

          const selectedTime = dayjs(value)

          end = selectedTime.isBefore(minTime) ? minTime : selectedTime
        } else {
          end = selectedDate
        }
      } else {
        if (selectedDate.isSameOrBefore(form.values.start_at)) {
          end = dayjs(form.values.start_at).add(30, 'minute')
        } else {
          end = selectedDate
        }
      }

      const minutes = end.minute()
      const minutesToAdd = minutes % 15 === 0 ? 0 : 15 - (minutes % 15)
      end = end.add(minutesToAdd, 'minute')

      const formattedEnd = end.format('YYYY-MM-DDTHH:mm:00')

      form.setFieldValue('end_at', formattedEnd)
    },
    [form.values.start_at, form.values.end_at, form.values.start_at_now]
  )

  const getNextMinuteIntervalFromNow = (intervalMinutes = 15) => {
    const now = dayjs()
    const currentMinute = now.minute()
    const remainder = currentMinute % intervalMinutes

    const minutesToAdd =
      remainder === 0 ? intervalMinutes : intervalMinutes - remainder

    return now.add(minutesToAdd, 'minute').startOf('minute').format('HH:mm')
  }

  const minProps = useMemo(() => {
    const startAtDateOnly = dayjs(form.values.start_at).format('YYYY-MM-DD')
    const endAtDateOnly = dayjs(form.values.end_at).format('YYYY-MM-DD')

    const roundToInterval = (date: dayjs.Dayjs, intervalMinutes = 15) => {
      const minutes = date.minute()
      const remainder = minutes % intervalMinutes
      const newMinutes =
        remainder === 0 ? minutes : minutes + (intervalMinutes - remainder)

      if (newMinutes === 60) {
        return date.add(1, 'hour').minute(0)
      }

      return date.minute(newMinutes)
    }

    return {
      startAt: {
        minDate: dayjs().toDate(),
        minTime:
          form.values.start_at && dayjs(startAtDateOnly).isToday()
            ? getNextMinuteIntervalFromNow(15)
            : undefined,
      },
      endAt: {
        minDate: form.values.start_at
          ? dayjs(form.values.start_at).toDate()
          : dayjs().toDate(),
        minTime:
          form.values.start_at &&
          form.values.end_at &&
          dayjs(startAtDateOnly).isSame(endAtDateOnly)
            ? roundToInterval(
                dayjs(form.values.start_at).add(30, 'minute')
              ).format('HH:mm')
            : dayjs().isSame(endAtDateOnly, 'day')
              ? roundToInterval(dayjs().add(30, 'minutes')).format('HH:mm')
              : undefined,
      },
    }
  }, [form.values.start_at, form.values.end_at, form.values.start_at_now])

  useEffect(() => {
    if (form.values.start_at_now) {
      form.setFieldValue(
        'start_at',
        dayjs().format(`YYYY-MM-DDT${getNextHourMinus15()}:00`)
      )
    }
  }, [form.values.start_at_now])

  return (
    <Card withBorder>
      <Text fw={500}>{t('create_insurance.dates.label_dates')}</Text>
      <Text c='dimmed' size='sm'>
        {t('create_insurance.dates.card_dates_selection_description')}
      </Text>
      <Flex
        gap='md'
        align='flex-start'
        mt='md'
        wrap={isMobile ? 'wrap' : undefined}
        justify='stretch'
      >
        <DateTimePickerInput
          fullWidth
          value={form.values.start_at_now ? '' : form.values.start_at}
          size={isMobile ? 'sm' : 'md'}
          label={isMobile ? t('label_pickup') : undefined}
          minTime={minProps.startAt.minTime}
          minDate={minProps.startAt.minDate}
          disabled={form.values.start_at_now}
          placeholderDate={
            form.values.start_at_now
              ? dayjs().format(
                  i18n.language === 'en' ? 'MMM DD YYYY' : 'DD MMMM YYYY'
                )
              : undefined
          }
          placeholderTime={
            form.values.start_at_now
              ? t('create_insurance.dates.start_now')
              : undefined
          }
          onChange={(value) => handleChangeStartAt(value)}
          key={form.values.start_at}
        />
        {!isMobile && (
          <Box mt='sm'>
            <Icon icon={faArrowRight} color='gray.7' />
          </Box>
        )}
        <DateTimePickerInput
          fullWidth
          value={form.values.end_at}
          size={isMobile ? 'sm' : 'md'}
          label={isMobile ? t('label_return') : undefined}
          minTime={minProps.endAt.minTime}
          minDate={minProps.endAt.minDate}
          onChange={(value) => {
            handleChangeEndAt(value)
            track('INSURANCE_FORM__DATES_SET', {})
          }}
          key={form.values.end_at}
        />
      </Flex>
      <Checkbox
        label={t('create_insurance.dates.label_start_now')}
        checked={form.values.start_at_now}
        onChange={(event) => {
          const checked = event.currentTarget.checked

          form.setFieldValue('start_at_now', checked)
        }}
        mt='lg'
      />
    </Card>
  )
}
